import React, {useEffect, useRef, useState} from 'react';
import {CustomDomain} from '../../api/types/organization/CustomDomain';
import {useSelector} from 'react-redux';
import {RootState} from '../../store';
import {CrudImpl as CustomDomainCrudImpl, CustomDomainCrud} from '../../api/org/customDomains/api';
import {CrudShim as CustomDomainCrudShim} from '../../api/org/customDomains/shim';
import {ToastContainer} from 'react-toastify';
import {StorageSource} from '../../api/types/organization/StorageSource';
import {CrudImpl as StorageSourceCrudImpl, StorageSourceCrud} from '../../api/org/storageSource/api';
import {CrudShim as StorageSourceCrudShim} from '../../api/org/storageSource/shim';
import {Outlet} from 'react-router-dom';

const shim = false;

type LoadingState = 'idle' | 'pending' | 'resolved' | 'rejected';

export const CustomDomainScreenContext = React.createContext<{
    api?: CustomDomainCrud;
    list: CustomDomain[];
    storageSourceList: StorageSource[];
    onDomainUpdate(domain: CustomDomain): void;
    onDomainAdd(domain: CustomDomain): void;
    onDomainDelete(domain: CustomDomain): void;
}>({
    api: undefined,
    list: [],
    storageSourceList: [],
    onDomainUpdate: () => {},
    onDomainAdd: () => {},
    onDomainDelete(domain: CustomDomain) {},
});

export default function CustomDomainScreenLayout() {
    const [list, setList] = useState<CustomDomain[]>([]);
    const [storageSourceList, setStorageSourceList] = useState<StorageSource[]>([]);
    const [loading, setLoading] = useState<LoadingState>('idle');
    const [error, setError] = useState<Error | undefined>(undefined);
    const userProfile = useSelector((state: RootState) => state.userProfile);
    const api = useRef<CustomDomainCrud | undefined>(undefined);
    const storageApi = useRef<StorageSourceCrud | undefined>(undefined);

    useEffect(() => {
        if (api.current === undefined || api.current?.orgId !== userProfile.currentOrganization.uid) {
            api.current = shim
                ? new CustomDomainCrudShim(userProfile.currentOrganization.uid)
                : new CustomDomainCrudImpl(userProfile.currentOrganization.uid);
        }
        if (storageApi.current === undefined || storageApi.current?.orgId !== userProfile.currentOrganization.uid) {
            storageApi.current = shim
                ? new StorageSourceCrudShim(userProfile.currentOrganization.uid)
                : new StorageSourceCrudImpl(userProfile.currentOrganization.uid);
        }
        if (loading === 'idle') {
            setLoading('pending');
            Promise.all([api.current.list(), storageApi.current.list()])
                .then(([data, storageData]) => {
                    setList(data);
                    setStorageSourceList(storageData);
                    setLoading('resolved');
                })
                .catch((e) => {
                    setError(e);
                    setLoading('rejected');
                });
        }
    }, [loading, userProfile.currentOrganization.uid, list]);

    return loading === 'rejected' ? (
        <>
            <div className={'flex flex-row items-center justify-center p-4 text-red-500'}>
                {error?.message ?? 'Unknown error'}
            </div>
        </>
    ) : loading === 'idle' || loading === 'pending' ? (
        <div className={'flex flex-row items-center justify-center p-4 text-gray-500'}>Loading...</div>
    ) : loading === 'resolved' ? (
        <>
            <ToastContainer />
            <CustomDomainScreenContext.Provider
                value={{
                    list,
                    storageSourceList,
                    api: api.current,
                    onDomainUpdate(domain: CustomDomain) {
                        setList((prev) => prev.map((d) => (d.hostname === domain.hostname ? domain : d)));
                    },
                    onDomainAdd(domain: CustomDomain) {
                        setList((prev) => [...prev, domain]);
                    },
                    onDomainDelete(domain: CustomDomain) {
                        setList((prev) => prev.filter((d) => d.hostname !== domain.hostname));
                    },
                }}>
                <Outlet />
            </CustomDomainScreenContext.Provider>
        </>
    ) : null;
}
